import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { MatchingPatient, MatchingPatientsParams } from '@/types/patient-api';

/**
 * GET /api/Patients/Validate?<query> — duplicate-record check. Flutter uses
 * GET with URL-encoded query, including `+` → `%2B` for phone numbers.
 * Empty params yield no query string and the call is disabled.
 *
 * Consumers (Personal Details, EmiratesId scan) debounce 500 ms before
 * setting the params so this fires once per stable input.
 */
export function useMatchingPatients(params: MatchingPatientsParams | null) {
  return useQuery({
    queryKey: ['matching-patients', params],
    queryFn: ({ signal }) => {
      const qs = new URLSearchParams();
      if (params?.idType) qs.set('IDType', params.idType);
      if (params?.idNumber) qs.set('IDNumber', params.idNumber);
      if (params?.firstName) qs.set('FirstName', params.firstName);
      if (params?.lastName) qs.set('LastName', params.lastName);
      if (params?.gender) qs.set('Gender', params.gender);
      if (params?.birthDate) qs.set('BirthDate', params.birthDate);
      if (params?.emailAddress) qs.set('EmailAddress', params.emailAddress);
      if (params?.contactNumber) qs.set('ContactNumber', params.contactNumber);
      return get<MatchingPatient[]>(
        `/api/Patients/Validate?${qs.toString()}`,
        { signal },
      );
    },
    enabled: !!params && hasAnyFilter(params),
  });
}

function hasAnyFilter(p: MatchingPatientsParams): boolean {
  return Boolean(
    p.idType ||
      p.idNumber ||
      p.firstName ||
      p.lastName ||
      p.gender ||
      p.birthDate ||
      p.emailAddress ||
      p.contactNumber,
  );
}
